import { calculateAmountOutMinimum, } from '@trinity/pharaoh-common';
import { ZeroAddress, ethers } from 'ethers';
import { PoolService, PriceService, QuoteService, SwapService, TokenService } from './services';
/**
 * Main PharaohSwap class for interacting with Pharaoh DEX
 */
export class PharaohSwap {
    /**
     * Creates a new PharaohSwap instance
     *
     * @param runner - Ethers ContractRunner (Signer with provider)
     * @param pharaohAddresses - Object containing Pharaoh contract addresses
     * @param tokenAddresses - Map of token symbols to addresses
     */
    constructor(runner, pharaohAddresses, tokenAddresses) {
        this.tokenService = new TokenService(runner, tokenAddresses);
        this.poolService = new PoolService(runner, pharaohAddresses.V3_FACTORY);
        this.quoteService = new QuoteService(runner, pharaohAddresses.QUOTER_V2, pharaohAddresses.QUOTER_V1, this.tokenService);
        this.swapService = new SwapService(runner, pharaohAddresses.V3_SWAP_ROUTER, this.tokenService);
        this.priceService = new PriceService();
    }
    /**
     * Helper method to check if token is native and return appropriate token for decimals
     */
    getTokenForDecimals(tokenAddress) {
        const isNative = tokenAddress.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' ||
            tokenAddress.toLowerCase() === '0x0000000000000000000000000000000000000000';
        if (isNative) {
            const wrappedTokens = this.tokenService.getNativeWrappedTokenAddresses();
            return wrappedTokens[0];
        }
        return tokenAddress;
    }
    /**
     * Get quote for a token pair
     *
     * @param tokenInAddressOrSymbol - Input token address or symbol
     * @param tokenOutAddressOrSymbol - Output token address or symbol
     * @param amountIn - Amount to swap (string or bigint)
     * @param options - Optional swap configuration
     * @returns Quote information
     */
    async getQuote(tokenInAddressOrSymbol, tokenOutAddressOrSymbol, amountIn, options = {}) {
        // Resolve token addresses from symbols if needed
        const tokenIn = this.tokenService.tokenAddresses[tokenInAddressOrSymbol] || tokenInAddressOrSymbol;
        const tokenOut = this.tokenService.tokenAddresses[tokenOutAddressOrSymbol] || tokenOutAddressOrSymbol;
        // Get appropriate token addresses for decimals (handle native tokens)
        const tokenInForDecimals = this.getTokenForDecimals(tokenIn);
        // Parse amount if string
        const inDecimals = await this.tokenService.getTokenDecimals(tokenInForDecimals);
        const parsedAmountIn = typeof amountIn === 'string' ? ethers.parseUnits(amountIn, inDecimals) : amountIn;
        // Get quote
        return await this.quoteService.getSwapQuote(tokenIn, tokenOut, parsedAmountIn, options.feeTier || 500);
    }
    /**
     * Execute swap with quote
     *
     * @param tokenInAddressOrSymbol - Input token address or symbol
     * @param tokenOutAddressOrSymbol - Output token address or symbol
     * @param amountIn - Amount to swap (string or bigint)
     * @param options - Optional swap configuration
     * @returns Transaction hash and amount out
     */
    async swap(tokenInAddressOrSymbol, tokenOutAddressOrSymbol, amountIn, options = {}) {
        // Resolve token addresses from symbols if needed
        const tokenIn = this.tokenService.tokenAddresses[tokenInAddressOrSymbol] || tokenInAddressOrSymbol;
        const tokenOut = this.tokenService.tokenAddresses[tokenOutAddressOrSymbol] || tokenOutAddressOrSymbol;
        // Get appropriate token addresses for decimals (handle native tokens)
        const tokenInForDecimals = this.getTokenForDecimals(tokenIn);
        const tokenOutForDecimals = this.getTokenForDecimals(tokenOut);
        // Parse amount if string
        const inDecimals = await this.tokenService.getTokenDecimals(tokenInForDecimals);
        const parsedAmountIn = typeof amountIn === 'string' ? ethers.parseUnits(amountIn, inDecimals) : amountIn;
        // Check if pool exists and has liquidity
        const { feeTier = 500 } = options;
        const poolAddress = await this.poolService.getPoolAddress(tokenInForDecimals, tokenOutForDecimals, feeTier);
        console.log(`Pool address: ${poolAddress}`);
        if (poolAddress === ZeroAddress) {
            throw new Error(`Pool does not exist for this token pair with fee ${(feeTier / 10000) * 100}%`);
        }
        const poolInfo = await this.poolService.getPoolInfo(poolAddress);
        if (!poolInfo.hasLiquidity) {
            throw new Error(`Pool has no liquidity for this token pair with fee ${feeTier / 10000}%`);
        }
        // Get quote
        const quote = await this.quoteService.getSwapQuote(tokenIn, tokenOut, parsedAmountIn, feeTier);
        // Calculate minimum amount out with slippage
        const slippageTolerance = options.slippageTolerance || 0.5;
        const amountOutMinimum = calculateAmountOutMinimum(quote.amountOut, slippageTolerance / 100);
        // Execute swap
        return await this.swapService.swap(tokenIn, tokenOut, parsedAmountIn, amountOutMinimum, options);
    }
    /**
     * Swap using token pair string (e.g., "USDC-USDt")
     *
     * @param pairString - Token pair in format "USDC-USDt"
     * @param amountIn - Amount to swap (string or bigint)
     * @param options - Optional swap configuration
     * @returns Transaction hash and amount out
     */
    async swapWithPair(pairString, amountIn, options = {}) {
        const { fromToken, toToken } = this.tokenService.parseTokenPair(pairString);
        return this.swap(fromToken, toToken, amountIn, options);
    }
    /**
     * Get quote using token pair string (e.g., "USDC-USDt")
     *
     * @param pairString - Token pair in format "USDC-USDt"
     * @param amountIn - Amount to swap (string or bigint)
     * @param options - Optional swap configuration
     * @returns Quote information
     */
    async getQuoteWithPair(pairString, amountIn, options = {}) {
        const { fromToken, toToken } = this.tokenService.parseTokenPair(pairString);
        return this.getQuote(fromToken, toToken, amountIn, options);
    }
    /**
     * Get token price from Odos API
     *
     * @param tokenAddressOrSymbol - Token address or symbol
     * @param chainId - The chain ID (defaults to Avalanche)
     * @param currencyId - The currency to get the price in (defaults to USD)
     * @returns Token price information
     */
    async getTokenPrice(tokenAddressOrSymbol, chainId, currencyId) {
        // Resolve token address from symbol if needed
        const tokenAddress = this.tokenService.tokenAddresses[tokenAddressOrSymbol] || tokenAddressOrSymbol;
        const tokenAddressForPrice = this.getTokenForDecimals(tokenAddress);
        return this.priceService.getTokenPrice(tokenAddressForPrice, chainId, currencyId);
    }
}
// Export types and services for direct access
export * from './services';
export * from './types';
